import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import {
  Stack,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Alert,
  AlertTitle,
} from "@mui/material";
import {
  useGetUserQuery,
  useUpdateUserEmailMutation,
  useDeleteUserMutation,
  useUpdateUserPasswordMutation,
} from "./userApiSlice";
import { setCredentials } from "../auth/authSlice";
import { storage } from "../../firebase";
import { ref, deleteObject } from "firebase/storage";
const PWD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/;
function ManageAccount() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [focus, setFocus] = useState(false);
  const [pw, setPw] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [error, setError] = useState({
    newPassword: "",
    confirmNewPassword: "",
  });
  const { data: user } = useGetUserQuery("User");
  const [
    updateEmail,
    {
      isSuccess: isUpdateEmailSuccess,
      isError: isUpdateEmailError,
      error: errorUpdateEmail,
      reset: resetUpdateEmail,
    },
  ] = useUpdateUserEmailMutation("User");
  const [
    updatePassword,
    {
      isSuccess: isUpdatePasswordSuccess,
      isError: isUpdatePasswordError,
      error: errorUpdatePassword,
      reset: resetUpdatePassword,
    },
  ] = useUpdateUserPasswordMutation();
  const [
    deleteUser,
    { isSuccess: isDelSuccess, isError: isDelError, error: errorDel },
  ] = useDeleteUserMutation();

  function handleOnChangeEmail(event) {
    setEmail(event.target.value);
  }
  function handleOnFocusChange() {
    setFocus(true);
  }
  function handleOnBlurChange() {
    setFocus(!focus);
  }
  function handleOnChangePw(event) {
    const { value, name } = event.target;
    setPw((preValue) => {
      return {
        ...preValue,
        [name]: value,
      };
    });
    validateInput(event);
  }
  function validateInput(e) {
    let { name, value } = e.target;
    setError((prev) => {
      const stateObj = { ...prev, [name]: "" };
      switch (name) {
        case "newPassword":
          if (!value) {
            stateObj[name] = "Please enter Password!";
          } else if (pw.confirmNewPassword && value !== pw.confirmNewPassword) {
            stateObj["confirmPassword"] =
              "Password and Confirm Password does not match!";
          } else if (!PWD_REGEX.test(value)) {
            stateObj[name] =
              "Password must contain Minimum eight characters, at least one uppercase letter, one lowercase letter, one number and one special character!";
          } else {
            stateObj["confirmPassword"] = pw.confirmNewPassword
              ? ""
              : error.confirmNewPassword;
          }
          break;

        case "confirmNewPassword":
          if (!value) {
            stateObj[name] = "Please enter Confirm Password!";
          } else if (pw.newPassword && value !== pw.newPassword) {
            stateObj[name] = "Password and Confirm Password does not match!";
          }
          break;
        default:
          break;
      }

      return stateObj;
    });
  }
  async function updateUserEmail(e) {
    e.preventDefault();
    handleOnBlurChange();
    try {
      const { accessToken } = await updateEmail({ newEmail: email }).unwrap();
      dispatch(setCredentials({ accessToken }));
    } catch (error) {
      console.log(error);
    }
  }
  async function handleSubmitPassword(e) {
    e.preventDefault();
    if (
      !error.newPassword &&
      !error.confirmNewPassword &&
      pw.newPassword === pw.confirmNewPassword
    ) {
      await updatePassword({
        oldPassword: pw.currentPassword,
        newPassword: pw.newPassword,
      });
    }
  }
  async function onDelAccount() {
    try {
      if (user.avatar.data) {
        const delImgRef = ref(
          storage,
          `images/avatars/${user._id}/${user.avatar.name}`
        );
        await deleteObject(delImgRef);
      }
      await deleteUser({ id: user._id });
    } catch (error) {
      console.log(error);
    }
  }
  useEffect(() => {
    if (isUpdateEmailSuccess) {
      setEmail("");
    }
    if (isUpdatePasswordSuccess) {
      setPw({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
    }
    if (isDelSuccess) {
      navigate("/login");
    }
  }, [isUpdateEmailSuccess, isUpdatePasswordSuccess, isDelSuccess, navigate]);
  return (
    <div>
      <header className="account-header">
        <h1>Account</h1>
        <p>Edit your account settings and change your password here.</p>
      </header>
      <form className="account-email" onSubmit={updateUserEmail}>
        <Stack direction="row" sx={{ marginBottom: 4 }}>
          <TextField
            type="email"
            label="Email"
            name="email"
            variant="outlined"
            placeholder={user?.email}
            required
            fullWidth
            onChange={handleOnChangeEmail}
            value={email}
            onFocus={handleOnFocusChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    type={!focus ? "button" : "submit"}
                    sx={{ ml: 2 }}
                    aria-label="edit"
                    size="large"
                    onClick={() => {
                      !focus && handleOnFocusChange();
                    }}
                  >
                    {focus ? <CheckOutlinedIcon /> : <EditIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </form>
      {isUpdateEmailSuccess && (
        <Alert
          variant="filled"
          severity="success"
          sx={{
            p: "35px",
            width: "100%",
          }}
          onClose={resetUpdateEmail}
        >
          New email set successfully.
        </Alert>
      )}
      {isUpdateEmailError && (
        <Alert
          variant="filled"
          severity="error"
          sx={{
            p: "35px",
            width: "100%",
          }}
          onClose={resetUpdateEmail}
        >
          {errorUpdateEmail?.data?.message}
        </Alert>
      )}
      {isUpdatePasswordSuccess && (
        <Alert
          variant="filled"
          severity="success"
          sx={{
            p: "35px",
            width: "100%",
          }}
          onClose={resetUpdatePassword}
        >
          New password changed successfully.
        </Alert>
      )}
      {isUpdatePasswordError && (
        <Alert
          variant="filled"
          severity="error"
          sx={{
            p: "35px",
            width: "100%",
          }}
          onClose={resetUpdatePassword}
        >
          {errorUpdatePassword?.data?.message}
        </Alert>
      )}
      <form className="account-pw" onSubmit={handleSubmitPassword}>
        <Stack spacing={2} sx={{ marginBottom: 4 }}>
          <TextField
            type="password"
            label="Current Password"
            name="currentPassword"
            variant="outlined"
            required
            fullWidth
            onChange={(e) => handleOnChangePw(e)}
            value={pw.currentPassword}
          />
          <TextField
            type="password"
            label="New Password"
            name="newPassword"
            variant="outlined"
            required
            fullWidth
            onChange={(e) => handleOnChangePw(e)}
            value={pw.newPassword}
            error={!!error.newPassword}
            helperText={error.newPassword}
          />
          <TextField
            type="password"
            label="Confirm New Password"
            name="confirmNewPassword"
            variant="outlined"
            required
            fullWidth
            onChange={(e) => handleOnChangePw(e)}
            value={pw.confirmNewPassword}
            error={!!error.confirmNewPassword}
            helperText={error.confirmNewPassword}
          />
          <Button
            className="reset-pw-btn"
            variant="outlined"
            type="submit"
            size="large"
          >
            Change password
          </Button>
        </Stack>
      </form>
      <div className="delete">
        <Alert
          variant="filled"
          severity={!isDelError ? "warning" : "error"}
          sx={{
            p: "8px",
            width: "100%",
          }}
        >
          <AlertTitle>{!isDelError ? "Warning" : "Delete Error"}</AlertTitle>
          {!isDelError ? (
            <strong>
              If you delete your account, your notes and labels also will be
              deleted!
            </strong>
          ) : (
            <strong>Unable to delete account</strong>
          )}
        </Alert>
        <Button
          className="delete-btn"
          variant="outlined"
          type="button"
          size="large"
          onClick={onDelAccount}
        >
          Delete
        </Button>
      </div>
    </div>
  );
}

export default ManageAccount;
